// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';
import {promisifyModal} from 'common/components/modal/utils';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';

import {template} from './template';
import css from './index.inline.styl';

export type UpsellModalAttrs = {
  closeable?: boolean;
  description?: string;
  'feature-gate-type'?: string;
  header: string;
  'learn-more-button-url'?: string;
  'learn-more-link-url'?: string;
  open: boolean;
  'upgrade-text'?: string;
  'upgrade-url'?: string;
  'bold-text'?: string;
  'callout-top'?: string;
  'callout-middle'?: string;
  'callout-bottom'?: string;
};

export type UpsellModalProps = {
  backgroundImage?: string;
};

export type UpsellModalState = {
  open: boolean;
  backgroundImage: string;
};

export class UpsellModal extends Component<UpsellModalState, UpsellModalAttrs, unknown, unknown, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<UpsellModalAttrs> {
    return {
      closeable: {type: `boolean`, description: `If true, modal can be closed with a "Cancel" button`},
      description: {type: `string`, description: `Main body text for the modal`},
      'feature-gate-type': {type: `string`, description: `Feature gate that we are trying to upsell`},
      header: {type: `string`, description: `Top header text for the modal`},
      'learn-more-button-url': {
        type: `string`,
        description: `Optional url - if set, add a "Learn More" button that links to this url`,
      },
      'learn-more-link-url': {
        type: `string`,
        description: `Optional url - if set, add a "Learn More" link at the end of the description that links to this url`,
      },
      open: {type: `boolean`, description: `If True, start with the modal open`},
      'upgrade-text': {type: `string`, description: `String for the upgrade CTA`},
      'upgrade-url': {type: `string`, default: `/pricing/`, description: `URL for the upgrade CTA`},
      'bold-text': {type: `string`, default: null, description: `Optional string for bold text below the description`},
      'callout-top': {
        type: `string`,
        default: null,
        description: `Optional text that will be shown as a callout where the learn more button would be`,
      },
      'callout-middle': {
        type: `string`,
        default: null,
        description: `Optional text that will be shown as a callout where the learn more button would be`,
      },
      'callout-bottom': {
        type: `string`,
        default: null,
        description: `Optional text that will be shown as a callout where the learn more button would be`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      helpers: {
        getCalloutStrings: () => {
          const callouts = [];
          [`callout-top`, `callout-middle`, `callout-bottom`].forEach((value) => {
            // These are all valid attrs, so we can ts-ignore
            // @ts-ignore
            if (this.attr(value)) {
              // @ts-ignore
              callouts.push(this.attr(value));
            }
          });
          return callouts;
        },
        handleModalChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.updateVisibility(false);
          }
          propagateEvent(this, ev);
        },
        handleClickCancel: () => this.updateVisibility(false),
        handleClickUpgrade: () => {
          trackUpsellEvent(`Click Upsell`, this.attr(`feature-gate-type`));
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    trackUpsellEvent(`Upsell Modal Shown`, this.attr(`feature-gate-type`));
  }

  disconnectedCallback() {
    this.updateVisibility(false);
    super.disconnectedCallback();
  }

  updateVisibility(open) {
    this.update({open});
    if (open) {
      trackUpsellEvent(`View Upsell`, this.attr(`feature-gate-type`));
    }

    const chromeHeaderEl = document.querySelector<HTMLElement>(`mp-chrome-header`);
    if (chromeHeaderEl) {
      // mp-chrome-header exists when in the context of the site, but not for standalone, tests, etc...
      // TODO: Dirty GOOT hack to allow nav access when upsell modal is present.
      chromeHeaderEl.style.zIndex = open ? `9999` : ``;
    }
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `open`) {
      this.updateVisibility(this.attr(name));
    }
  }

  set backgroundImage(backgroundImage) {
    this.update({backgroundImage});
  }
}

type JsxProps = {
  attrs: UpsellModalAttrs;
  props: UpsellModalProps;
};

export const UpsellModalEl = defineAndMakeJsxFactory(`mp-upsell-modal`, UpsellModal);
export const UpsellModalReact = panel2react<JsxProps>(`mp-upsell-modal`, UpsellModal);

export function upsellModalPromise(
  attrs: Partial<UpsellModalAttrs>,
  props: UpsellModalProps = {},
): Promise<CustomEvent> {
  return promisifyModal({
    tagName: `mp-upsell-modal`,
    attrs,
    props,
    closeEvents: [`close`],
  });
}
